// src/pages/RegisterPage.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";

import AgentBlaynaLogo from "../assets/Agent-Blayna-Logo.png";
import { authService } from "../api/authService";
import { setAuth } from "../utils/auth";
import { API_BASE_URL, API_ENDPOINTS } from "../config";

// Keep in sync with TERMS_VERSION in the backend environment.
const TERMS_VERSION = "2026-01-05";

const RegisterPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchParams] = useSearchParams();

  const inviteCode = searchParams.get("code") || "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [orgName, setOrgName] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(!!inviteCode);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [emailLocked, setEmailLocked] = useState(false);

  // Open signup (no invite code): the agent picks their market, and capacity is
  // enforced server-side, so we may get a waitlist placement instead of tokens.
  const isOpenSignup = !inviteCode;
  const [markets, setMarkets] = useState([]);
  const [marketKey, setMarketKey] = useState("");
  const [waitlisted, setWaitlisted] = useState(null);

  // Fetch invite info on mount
  useEffect(() => {
    if (!inviteCode) return;
    setInviteLoading(true);
    fetch(`${API_BASE_URL}${API_ENDPOINTS.INVITE_INFO}?code=${encodeURIComponent(inviteCode)}`)
      .then(async (res) => {
        if (res.status === 404) throw new Error("Invalid or inactive invitation code.");
        if (res.status === 410) throw new Error("This invitation has expired or has already been used.");
        if (!res.ok) throw new Error("Could not verify invitation.");
        return res.json();
      })
      .then((info) => {
        setInviteInfo(info);
        if (info.email) { setEmail(info.email); setEmailLocked(true); }
        if (info.org_name) setOrgName(info.org_name);
      })
      .catch((err) => {
        setError(err.message || "Invalid invitation link.");
      })
      .finally(() => setInviteLoading(false));
  }, [inviteCode]);

  // Public market list, same source the request-access modal uses.
  useEffect(() => {
    if (!isOpenSignup) return;
    fetch(`${API_BASE_URL}${API_ENDPOINTS.ACCESS_REQUEST_MARKETS}`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setMarkets(Array.isArray(data) ? data : []))
      .catch(() => setMarkets([]));
  }, [isOpenSignup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (!acceptTerms) {
      setError("You must accept the Terms of Service.");
      return;
    }
    if (isOpenSignup && !marketKey) {
      setError("Please select the city you work in.");
      return;
    }

    setLoading(true);
    try {
      if (isOpenSignup) {
        const [city, state] = marketKey.split("|");
        const data = await authService.signup({
          email: email.trim(),
          password,
          name: name.trim(),
          organization_name: orgName.trim() || undefined,
          city,
          state,
          accept_terms: true,
          terms_version: TERMS_VERSION,
        });

        // A full or unopened market returns a waitlist placement, not an account.
        if (data.status === "waitlisted") {
          setWaitlisted(data.message);
          return;
        }

        setAuth(data);
        navigate("/onboarding", { replace: true });
        return;
      }

      const data = await authService.register({
        email: email.trim(),
        password,
        name: name.trim(),
        invite_code: inviteCode,
        org_name: orgName.trim() || undefined,
        accept_terms: true,
        terms_version: TERMS_VERSION,
      });
      setAuth(data);
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError(err.detail || err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(11, 15, 26, 0.10)",
    fontSize: 14,
    backgroundColor: "rgba(255,255,255,0.7)",
    color: "#0B0F1A",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
        padding: isMobile ? "16px 12px" : "24px 12px",
        overflow: "hidden",
      }}
    >
      {/* Multi-layer gradient background (matches landing page) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `
            radial-gradient(1100px circle at 16% 0%, rgba(45, 91, 255, 0.15), transparent 55%),
            radial-gradient(1100px circle at 88% 12%, rgba(122, 92, 255, 0.10), transparent 60%),
            radial-gradient(1200px circle at 50% 110%, rgba(0, 0, 0, 0.06), transparent 60%),
            linear-gradient(180deg, #F7F8FA 0%, #F3F4F7 40%, #F7F8FA 100%)
          `,
        }}
      />
      {/* Subtle grain texture */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.05,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,0,0,.35) 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: isMobile ? "420px" : "980px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          borderRadius: isMobile ? "16px" : "24px",
          overflow: "hidden",
          boxShadow: `0 30px 110px rgba(11, 15, 26, 0.09), 0 4px 6px rgba(11, 15, 26, 0.02)`,
          backgroundColor: "rgba(255, 255, 255, 0.82)",
          backdropFilter: "blur(18px) saturate(180%)",
          WebkitBackdropFilter: "blur(18px) saturate(180%)",
          border: "1px solid rgba(11, 15, 26, 0.10)",
        }}
      >
        {/* LEFT: Hero Panel - Hidden on mobile */}
        {!isMobile && (
          <div
            style={{
              flex: 1.1,
              padding: "32px 36px",
              borderRight: "1px solid rgba(11, 15, 26, 0.08)",
              background: "transparent",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                <img
                  src={AgentBlaynaLogo}
                  alt="AgentBlayna logo"
                  style={{ height: 40, width: "auto", display: "block" }}
                />
                <div
                  style={{
                    fontSize: 14.8,
                    fontWeight: 990,
                    letterSpacing: -0.3,
                    color: "#0B0F1A",
                  }}
                >
                  AgentBlayna
                </div>
              </div>

              <h1
                style={{
                  fontSize: 32,
                  lineHeight: 1.08,
                  margin: "0 0 10px",
                  fontWeight: 995,
                  letterSpacing: "-1.5px",
                  color: "#0B0F1A",
                }}
              >
                {isOpenSignup ? "Welcome to AgentBlayna" : "Welcome aboard!"}
              </h1>

              <p
                style={{
                  margin: "0 0 18px",
                  fontSize: 15.5,
                  color: "rgba(11, 15, 26, 0.68)",
                  lineHeight: 1.8,
                  maxWidth: 420,
                }}
              >
                {isOpenSignup
                  ? "Create your AgentBlayna account and get your AI-powered real estate workspace running today."
                  : "You've been invited to join AgentBlayna. Complete your registration to get started with your AI-powered real estate workspace."}
              </p>

              <div
                style={{
                  display: "grid",
                  gap: 10,
                  fontSize: 14,
                  color: "rgba(11, 15, 26, 0.72)",
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "999px",
                      backgroundColor: "#e0f2fe",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: "#0369a1",
                      marginTop: 2,
                    }}
                  >
                    1
                  </span>
                  <span>Create your account with a secure password</span>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "999px",
                      backgroundColor: "#eff6ff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: "#1d4ed8",
                      marginTop: 2,
                    }}
                  >
                    2
                  </span>
                  <span>Complete your quick onboarding setup</span>
                </div>

                <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "999px",
                      backgroundColor: "#ecfdf5",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      color: "#15803d",
                      marginTop: 2,
                    }}
                  >
                    3
                  </span>
                  <span>Start your 7-day free trial — no card required</span>
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: 11,
                color: "rgba(11, 15, 26, 0.40)",
                marginTop: 24,
              }}
            >
              &copy; {year} AgentBlayna &middot; Smarter Real Estate Starts Here
            </div>
          </div>
        )}

        {/* RIGHT: Register Form */}
        <div
          style={{
            flex: isMobile ? 1 : 0.9,
            padding: isMobile ? "24px 20px" : "32px 36px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
          }}
        >
          {/* Mobile Logo Header */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 24,
              }}
            >
              <img
                src={AgentBlaynaLogo}
                alt="AgentBlayna logo"
                style={{ height: 36, width: "auto", display: "block" }}
              />
              <div
                style={{
                  fontSize: 14.8,
                  fontWeight: 990,
                  letterSpacing: -0.3,
                  color: "#0B0F1A",
                }}
              >
                AgentBlayna
              </div>
            </div>
          )}

          <div
            style={{
              width: "100%",
              maxWidth: 360,
              padding: isMobile ? "20px 18px 24px" : "22px 22px 26px",
              borderRadius: isMobile ? 14 : 18,
              backgroundColor: "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              boxShadow: isMobile
                ? "0 8px 24px rgba(11, 15, 26, 0.06)"
                : `0 14px 32px rgba(11, 15, 26, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.6)`,
              border: "1px solid rgba(11, 15, 26, 0.10)",
            }}
          >
            <h2
              style={{
                fontSize: 20,
                margin: "0 0 6px",
                fontWeight: 900,
                letterSpacing: "-0.3px",
                color: "#0B0F1A",
              }}
            >
              Create your account
            </h2>
            <p
              style={{
                margin: 0,
                marginBottom: inviteInfo ? 10 : 16,
                fontSize: 13.5,
                color: "rgba(11, 15, 26, 0.55)",
              }}
            >
              {isOpenSignup
                ? "Start your 7-day free trial. No invite needed."
                : "Complete your registration to get started."}
            </p>

            {inviteInfo && inviteInfo.market_city && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "8px 10px",
                  borderRadius: 8,
                  backgroundColor: "#eff6ff",
                  border: "1px solid #dbeafe",
                  fontSize: 12,
                  color: "#1d4ed8",
                  fontWeight: 500,
                }}
              >
                Market: {inviteInfo.market_city}, {inviteInfo.market_state}
                {inviteInfo.org_name && <> &middot; Org: {inviteInfo.org_name}</>}
              </div>
            )}

            {error && (
              <div
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#b91c1c",
                  padding: "8px 10px",
                  borderRadius: 8,
                  fontSize: 12,
                  marginBottom: 12,
                  border: "1px solid #fecaca",
                }}
              >
                {error}
              </div>
            )}

            {inviteLoading && (
              <div style={{ textAlign: "center", padding: "16px 0", color: "#6b7280", fontSize: 13 }}>
                Verifying invitation...
              </div>
            )}

            {waitlisted && (
              <div
                style={{
                  padding: "14px 14px",
                  borderRadius: 10,
                  backgroundColor: "#eff6ff",
                  border: "1px solid #dbeafe",
                  fontSize: 13,
                  color: "#1d4ed8",
                  lineHeight: 1.5,
                }}
              >
                <strong style={{ display: "block", marginBottom: 4 }}>
                  You're on the list
                </strong>
                {waitlisted}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              style={{
                display: inviteLoading || waitlisted ? "none" : "grid",
                gap: 12,
                marginTop: 4,
              }}
            >
              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="name"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                >
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Maria Rodriguez"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="reg-email"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                >
                  Email
                </label>
                <input
                  id="reg-email"
                  type="email"
                  placeholder="you@yourbrokerage.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={emailLocked}
                  style={{
                    ...inputStyle,
                    ...(emailLocked
                      ? { backgroundColor: "#f3f4f6", color: "#6b7280" }
                      : {}),
                  }}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="reg-password"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                >
                  Password (min. 8 characters)
                </label>
                <input
                  id="reg-password"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="confirm-password"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                >
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••••"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <label
                  htmlFor="org-name"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                >
                  Organization / Brokerage (optional)
                </label>
                <input
                  id="org-name"
                  type="text"
                  placeholder="RE/MAX Elite"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  disabled={!!inviteInfo?.org_name}
                  style={{
                    ...inputStyle,
                    ...(inviteInfo?.org_name
                      ? { backgroundColor: "#f3f4f6", color: "#6b7280" }
                      : {}),
                  }}
                />
                {isOpenSignup && (
                  <span style={{ fontSize: 11.5, color: "rgba(11, 15, 26, 0.45)" }}>
                    Leave blank if you work independently &mdash; we'll use your name.
                  </span>
                )}
              </div>

              {isOpenSignup && (
                <div style={{ display: "grid", gap: 6 }}>
                  <label
                    htmlFor="market"
                    style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", fontWeight: 700 }}
                  >
                    Where do you work?
                  </label>
                  <select
                    id="market"
                    required
                    value={marketKey}
                    onChange={(e) => setMarketKey(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Select your city</option>
                    {markets.map((m) => (
                      <option key={`${m.city}|${m.state}`} value={`${m.city}|${m.state}`}>
                        {m.city}, {m.state}
                        {m.status === "full" ? " — waitlist" : ""}
                      </option>
                    ))}
                  </select>
                  <span style={{ fontSize: 11.5, color: "rgba(11, 15, 26, 0.45)" }}>
                    Each city has a limited number of agents. If yours is full you'll
                    join the waitlist.
                  </span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  marginTop: 2,
                }}
              >
                <input
                  id="terms"
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  style={{ marginTop: 3, accentColor: "#2D5BFF" }}
                />
                <label
                  htmlFor="terms"
                  style={{ fontSize: 12.5, color: "rgba(11, 15, 26, 0.60)", cursor: "pointer" }}
                >
                  I agree to the Terms of Service and Privacy Policy
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  marginTop: 4,
                  padding: "10px 12px",
                  borderRadius: 999,
                  background: "#0B0F1A",
                  color: "#ffffff",
                  border: "none",
                  fontSize: 15,
                  fontWeight: 900,
                  cursor: loading ? "default" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  boxShadow: "0 18px 50px rgba(11, 15, 26, 0.14)",
                  transition: "transform 0.08s ease, box-shadow 0.08s ease",
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = "translateY(1px)";
                  e.currentTarget.style.boxShadow =
                    "0 10px 30px rgba(11, 15, 26, 0.12)";
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 18px 50px rgba(11, 15, 26, 0.14)";
                }}
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <div
              style={{
                marginTop: 18,
                fontSize: 12,
                color: "#9ca3af",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <span>
                Already have an account?{" "}
                <span
                  onClick={() => navigate("/login")}
                  style={{
                    color: "#2D5BFF",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  Log in
                </span>
              </span>
              {!isOpenSignup && (
                <span>
                  No invite?{" "}
                  <span
                    onClick={() => navigate("/signup")}
                    style={{
                      color: "#2D5BFF",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    Sign up here
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Mobile Footer */}
          {isMobile && (
            <div
              style={{
                marginTop: 24,
                fontSize: 11,
                color: "rgba(11, 15, 26, 0.40)",
                textAlign: "center",
              }}
            >
              &copy; {year} AgentBlayna &middot; Smarter Real Estate
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
